import { Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FieldTree, form, required } from '@angular/forms/signals'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { AsetustenOletuksetService } from '../../_angular/service/asetusten-oletukset.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'

import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'

import { Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { LaskutusprofiiliService } from '../../_angular/service/lasku/laskutusprofiili.service'

interface PerusasetuksetLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  postitoimipaikka: string
  postinro: string
  maa: string | null
  aspaemail: string
  aspapuh: string
}

@Component({
  selector: 'app-perusasetukset',
  templateUrl: './perusasetukset.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PerusasetuksetComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake = form(
    signal<PerusasetuksetLomake>({ nimi: '', ytunnus: '', katuosoite: '', postitoimipaikka: '', postinro: '', maa: '', aspaemail: '', aspapuh: '' }),
    polku => {
      required(polku.nimi)
      required(polku.ytunnus)
      required(polku.katuosoite)
      required(polku.postitoimipaikka)
      required(polku.postinro)
      required(polku.maa)
      required(polku.aspaemail)
    },
  )
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('laskuasetukset', this.lomake)
  })

  asetukset: Laskuasetukset = null
  maat: Observable<Maa[]>

  constructor(
    private laskuService: LaskuService,
    private validationService: FormValidationService,
    private laskuKopioija: LaskuKopioija,
    private asetuksetOletuksetService: AsetustenOletuksetService,
    private errorHandler: ErrorHandler,
    private snackbar: MatSnackBar,
    private ladataanService: LadataanService,
    private translationService: LemonTranslationService,
    private maaService: MaaService,
    private _laskutusprofiiliService: LaskutusprofiiliService,
  ) {
    this.maat = this.maaService.kaikkiMaatObservable
  }

  ngOnInit() {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    // Bind to changes
    muutokset(this.lomake.nimi).subscribe(value => {
      this.asetukset.nimi = this.validationService.processValue(value)
    })
    muutokset(this.lomake.ytunnus).subscribe(value => {
      this.asetukset.ytunnus = this.validationService.processValue(value)
    })
    muutokset(this.lomake.katuosoite).subscribe(value => {
      this.asetukset.katuosoite = this.validationService.processValue(value)
    })
    muutokset(this.lomake.postitoimipaikka).subscribe(value => {
      this.asetukset.postitmp = this.validationService.processValue(value)
    })
    muutokset(this.lomake.postinro).subscribe(value => {
      this.asetukset.postinro = this.validationService.processValue(value)
    })
    muutokset(this.lomake.maa).subscribe(value => {
      this.asetukset.maakoodi = this.validationService.processValue(value)
    })
    muutokset(this.lomake.aspaemail).subscribe(value => {
      this.asetukset.replyto = this.validationService.processValue(value)
    })
    muutokset(this.lomake.aspapuh).subscribe(value => {
      this.asetukset.puhelin = this.validationService.processValue(value)
    })

    // Load data
    this._laskutusprofiiliService.valittuLaskutusprofiiliObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      asetukset => {
        // this.form.reset()
        if (asetukset) {
          this.asetukset = this.laskuKopioija.copyAsetukset(asetukset)
          this.asetuksetOletuksetService.poistaOletuksetAsetuksista(this.asetukset)
        } else {
          this.asetukset = {
            avain: null,
            nimi: null,
            ytunnus: null,
            katuosoite: null,
            postitmp: null,
            postinro: null,
            maakoodi: 'FIN',
            date: null,
            replyto: null,
            puhelin: null,
            bic: null,
            iban: null,
            pankki: null,
            base64Logo: null,
            ohjeet: null,
            slogan: null,
            piilotaYrityksenNimiLaskulla: false,
            summahuomautusPiilotettu: false,
            muistutusSpostiasetukset: {},
            spostiasetukset: {},
            tervetuloaNahty: true,
          }
        }

        this.asetukset.tervetuloaNahty = true

        asetaArvo(this.lomake.nimi, this.asetukset.nimi ?? '')
        asetaArvo(this.lomake.ytunnus, this.asetukset.ytunnus ?? '')
        asetaArvo(this.lomake.katuosoite, this.asetukset.katuosoite ?? '')
        asetaArvo(this.lomake.postitoimipaikka, this.asetukset.postitmp ?? '')
        asetaArvo(this.lomake.postinro, this.asetukset.postinro ?? '')
        asetaArvo(this.lomake.maa, this.asetukset.maakoodi ?? null)
        asetaArvo(this.lomake.aspaemail, this.asetukset.replyto ?? '')
        asetaArvo(this.lomake.aspapuh, this.asetukset.puhelin ?? '')
        this.lomake().reset()

        afterNextRender(() => this.nimiInput.focus(), { injector: this._injector })
      },
      err => {
        this.errorHandler.handleError(err)
      },
    )
  }

  onkoTallentamattomiaMuutoksia(): boolean {
    return this.lomake().dirty()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  save() {
    if (this.validationService.onkoLomakkeessaVirheita(this.lomake)) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
    } else {
      this.ladataanService.aloitaLataaminen()
      this.laskuService
        .updateLaskuasetukset(this.asetukset, this.asetukset.avain)
        .then(() => {
          const lokalisoitu = this.translationService.lokalisoi('laskuasetukset.tallennettu-onnistuneesti')
          this.snackbar.open(lokalisoitu, 'OK', { duration: 5000, verticalPosition: 'top' })
          this.ladataanService.lopetaLataaminen()
        })
        .catch(err => {
          this.ladataanService.lopetaLataaminen()
          this.errorHandler.handleError(err)
        })
    }
  }
}
